import { Transition } from '@headlessui/react';
import { useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import BurgerSVG from '@/assets/icons/burger.svg';
import CloseSVG from '@/assets/icons/close.svg';
import { Logo } from '@/components/common/logo';
import { useModal } from '@/components/common/modal';
import { Button, ButtonVariant } from '@/components/form/button';
import { SignedInMethod, useAuth } from '@/features/auth';
import { PasswordChangeModal } from '@/features/auth/components/password-change-modal';
import { UserSettingsModal } from '@/features/user-settings';
import { CameraCalibrationWizard } from '@/features/user-settings/components/camera-calibration-wizard';
import { openInNewTab } from '@/utils/open-in-newtab';

import { DesktopNavBarItems, MobileNavBarItems } from './navbar-items';
import { ProfileDropdown } from './profile-dropdown';

export const Navbar = () => {
  const { user, signout } = useAuth();
  const navigate = useNavigate();
  const { setTheme }: any = useTheme();
  const { i18n } = useTranslation();
  const [collapseNav, setcollapseNav] = useState(false);

  // a force update trick to re-render user settings UI
  // we pass the forceUpdateKey as a key to the "UserSettingsModal"
  const [forceUpdateKey, forceUpdate] = useReducer((x) => x + 1, 0);

  // hook to manage open/close state of user settings modal
  const { isOpen, toggle } = useModal();
  const { isOpen: calibrationIsOpen, toggle: calibrationToggle } = useModal();

  // hook to manage password change modal
  const { isOpen: passwordChangeIsOpen, toggle: passwordChangeToggle } = useModal();

  const handleOnLogout = () => {
    const signedInMethod = user?.signedInMethod;
    signout();
    navigate('/');

    // when user logout, we need to keep the right method selected in the login page
    // checkout features/auth/page/login.tsx | useEffect, for more details
    if (signedInMethod) setTheme(signedInMethod);
  };

  const handleOnBack = () => {
    forceUpdate();
  };

  // handle when user click on 'Manual' nav item
  const handleOnManual = () => {
    const manual: any = {
      [SignedInMethod.Wine]: {
        EN: 'https://www.oculyze.net/resources/fw-training',
        DE: 'https://www.oculyze.net/de/ressourcen/fw-training',
        ES: 'https://www.oculyze.net/resources/fw-training',
        IT: 'https://www.oculyze.net/resources/fw-training',
        FR: 'https://www.oculyze.net/resources/fw-training',
      },
      [SignedInMethod.Beer]: {
        EN: 'https://www.oculyze.net/resources/bb-training',
        DE: 'https://www.oculyze.net/de/ressourcen/bb-training',
        ES: 'https://www.oculyze.net/resources/bb-training',
        IT: 'https://www.oculyze.net/resources/bb-training',
        FR: 'https://www.oculyze.net/resources/bb-training',
      },
      [SignedInMethod.Ethanol]: {
        EN: 'https://www.oculyze.net/resources/bb-training',
        DE: 'https://www.oculyze.net/de/ressourcen/bb-training',
        ES: 'https://www.oculyze.net/resources/bb-training',
        IT: 'https://www.oculyze.net/resources/bb-training',
        FR: 'https://www.oculyze.net/resources/bb-training',
      },
    };

    const method = user?.signedInMethod ?? null;
    const url = method ? manual[method][i18n.language] : '';
    openInNewTab(url);
  };

  return (
    <>
      <UserSettingsModal
        isOpen={isOpen}
        toggle={toggle}
        key={forceUpdateKey}
        onCameraCalibration={calibrationToggle}
      />
      <PasswordChangeModal
        isOpen={passwordChangeIsOpen}
        toggle={passwordChangeToggle}
        logout={handleOnLogout}
      />
      <CameraCalibrationWizard
        isOpen={calibrationIsOpen}
        toggle={calibrationToggle}
        onBack={handleOnBack}
      />
      <header className="fixed top-0 left-0 right-0 z-40 flex justify-between w-full px-4 pt-4 bg-white md:pt-12 md:container md:mx-auto">
        <div className="flex items-center gap-12">
          <Logo />
          <DesktopNavBarItems onSettings={toggle} onManual={handleOnManual} />
        </div>

        {/* show profile dropdown on >md size screes */}
        <div id="step-profile-dropdown" className="items-center hidden gap-2 lg:flex">
          <span className="text-base font-medium">{user?.email}</span>
          <ProfileDropdown
            onSettings={toggle}
            onLogout={handleOnLogout}
            onPasswordChange={passwordChangeToggle}
          />
        </div>

        {/* nav collapse icon will display on mobile devices */}
        <NavBarCollapseButton
          open={collapseNav}
          onClick={() => setcollapseNav(!collapseNav)}
        />
      </header>

      {/* below navbar will only display on mobile device */}
      <Transition
        as="div"
        show={collapseNav}
        className="absolute left-0 right-0 z-40 flex flex-col h-full top-16 md:top-24 lg:hidden"
      >
        <Transition.Child
          as="div"
          enter="transition-transform origin-top duration-300"
          enterFrom="scale-y-0"
          enterTo="scale-y-100"
          leave="transition-transform origin-top duration-300"
          leaveFrom="scale-y-100"
          leaveTo="scale-y-0"
          className="flex-none"
        >
          <div className="h-full bg-white drop-shadow-xl">
            <MobileNavBarItems
              onSettings={toggle}
              onManual={handleOnManual}
              onLogout={handleOnLogout}
            />
          </div>
        </Transition.Child>
        <Transition.Child
          as="div"
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100 backdrop-blur"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="flex-1"
        ></Transition.Child>
      </Transition>
    </>
  );
};

const NavBarCollapseButton = ({
  open,
  onClick,
}: {
  open: boolean;
  onClick?: () => void;
}) => {
  return (
    <Button variant={ButtonVariant.Link} onClick={onClick} classnames="lg:hidden">
      <img src={open ? CloseSVG : BurgerSVG} alt="collapse" />
    </Button>
  );
};
